import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../api";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { optimizeCloudinaryUrl } from "../components/PageContent";

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return optimizeCloudinaryUrl(url);
};

const isUploadedVideo = (url) =>
  !!url && (url.startsWith("/api/uploads/") || /\.(mp4|mov|webm|m4v)$/i.test(url));

const youtubeId = (url) => {
  if (!url) return null;
  if (/^[A-Za-z0-9_-]{11}$/.test(url)) return url;
  const m =
    url.match(/[?&]v=([A-Za-z0-9_-]{11})/) ||
    url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/) ||
    url.match(/embed\/([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const CATEGORY_LABEL = {
  decks: "Decks",
  apparel: "Apparel",
  accessories: "Accessories",
  "limited-runs": "Limited Runs",
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [err, setErr] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [artist, setArtist] = useState(null);
  const [related, setRelated] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    let active = true;
    setProduct(null);
    setArtist(null);
    setRelated([]);
    setActiveIdx(0);
    setSelectedVariantId(null);
    setErr(null);

    api
      .get(`/shop/products/${slug}`)
      .then((r) => {
        if (!active) return;
        setProduct(r.data);
        // Artist
        if (r.data.artist_crew_slug) {
          api
            .get(`/crew/${r.data.artist_crew_slug}`)
            .then((cr) => active && setArtist(cr.data))
            .catch(() => {});
        }
        // Related — fetch all and filter (small dataset)
        const slugs = r.data.related_product_slugs || [];
        if (slugs.length) {
          api
            .get(`/shop/products?limit=48`)
            .then((pr) => {
              if (!active) return;
              const slugSet = new Set(slugs);
              const items = pr.data?.items || [];
              setRelated(items.filter((p) => slugSet.has(p.slug || p.handle)));
            })
            .catch(() => {});
        }
      })
      .catch(() => active && setErr("Product has skated off."));

    return () => {
      active = false;
    };
  }, [slug]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const arr = [];
    if (product.image) arr.push(product.image);
    for (const u of product.images || []) {
      if (!arr.includes(u)) arr.push(u);
    }
    return arr;
  }, [product]);

  if (err) {
    return (
      <div className="py-24 text-center" data-testid="product-not-found">
        <SEO title="Vanished" path={`/shop/${slug}`} noindex />
        <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-terra mb-3">✦ No record</div>
        <div className="font-western text-4xl mb-4">{err}</div>
        <Link
          className="inline-flex items-center gap-2 border-2 border-asphalt px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone transition-colors"
          to="/shop"
        >
          <ArrowLeft className="w-4 h-4" /> Back to the shop
        </Link>
      </div>
    );
  }
  if (!product)
    return (
      <div className="py-24 text-center font-mono text-xs uppercase tracking-[0.3em]">
        Polishing the wheels…
      </div>
    );

  const activeImage = gallery[activeIdx] || product.image;
  const yt = product.video_url && !isUploadedVideo(product.video_url) ? youtubeId(product.video_url) : null;

  return (
    <div className="bg-bone min-h-screen" data-testid="product-detail-page">
      <SEO
        title={product.name}
        description={product.tagline || product.description?.slice(0, 160)}
        image={resolveUrl(activeImage)}
        type="product"
        path={`/shop/${product.slug}`}
      />

      {/* Editorial hero */}
      <section className="border-b-4 border-asphalt" data-testid="pdp-hero">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-6 pb-2">
          <Link
            to="/shop"
            data-testid="pdp-back"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] hover:text-terra"
          >
            <ArrowLeft className="w-4 h-4" /> Back to shop
          </Link>
        </div>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-8 lg:py-12 grid lg:grid-cols-12 gap-10">
          {/* Gallery */}
          <div className="lg:col-span-7" data-testid="pdp-gallery">
            <div className="border-4 border-asphalt bg-card-light shadow-hard relative">
              <div className="aspect-square overflow-hidden bg-bone">
                {activeImage && (
                  <img
                    src={resolveUrl(activeImage)}
                    alt={`${product.name} — ${CATEGORY_LABEL[product.category] || product.category}`}
                    className="w-full h-full object-cover"
                    data-testid="pdp-main-image"
                  />
                )}
              </div>
              <div className="absolute -top-4 -left-4 bg-terra text-bone px-3 py-1 font-mono text-[11px] uppercase tracking-[0.25em] border-2 border-asphalt">
                Item № {product.id?.slice(0, 4).toUpperCase() || "0000"}
              </div>
              {!product.in_stock && (
                <div className="absolute top-4 right-4 bg-asphalt text-bone px-3 py-1 font-mono text-[11px] uppercase tracking-[0.25em] border-2 border-bone">
                  Sold Out
                </div>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="grid grid-cols-5 gap-3 mt-4" data-testid="pdp-thumbnails">
                {gallery.map((url, i) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    data-testid={`pdp-thumb-${i}`}
                    className={`aspect-square border-2 overflow-hidden bg-bone transition-all ${
                      i === activeIdx
                        ? "border-terra shadow-hard"
                        : "border-asphalt hover:-translate-y-0.5"
                    }`}
                    aria-label={`Show image ${i + 1} of ${product.name}`}
                  >
                    <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Buy box */}
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra mb-3">
              ✦ {CATEGORY_LABEL[product.category] || product.category}
            </div>
            <h1
              className="font-display text-5xl sm:text-6xl uppercase tracking-tighter leading-[0.95]"
              data-testid="pdp-name"
            >
              {product.name}
            </h1>
            {product.tagline && (
              <p className="font-western text-2xl text-terra mt-3" data-testid="pdp-tagline">
                {product.tagline}
              </p>
            )}
            {(() => {
              const variants = product.variants || [];
              const showVariantSelector = variants.length > 1;
              const selected =
                variants.find((v) => v.id === selectedVariantId) ||
                variants.find((v) => v.available) ||
                variants[0];
              const displayPrice =
                selected && typeof selected.price === "number" ? selected.price : product.price;
              const inStock = selected ? selected.available : product.in_stock;

              return (
                <>
                  <div className="border-y-2 border-asphalt my-6 py-5">
                    <div className="font-display text-4xl" data-testid="pdp-price">
                      ${Number(displayPrice || 0).toFixed(2)}
                    </div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink mt-1">
                      {inStock
                        ? "In stock · ships from the dog house"
                        : "Out of stock — check back next week"}
                    </div>
                  </div>
                  <p className="font-body text-base leading-relaxed text-ink mb-8" data-testid="pdp-description">
                    {product.description}
                  </p>
                  {showVariantSelector && (
                    <div className="mb-6" data-testid="pdp-variants">
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2">
                        Pick your poison
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {variants.map((v) => {
                          const active = (selected?.id || "") === v.id;
                          const disabled = !v.available;
                          return (
                            <button
                              key={v.id}
                              type="button"
                              disabled={disabled}
                              onClick={() => setSelectedVariantId(v.id)}
                              data-testid={`pdp-variant-${v.id}`}
                              className={`px-4 py-2 border-2 border-asphalt font-mono text-xs uppercase tracking-[0.2em] transition-all ${
                                active
                                  ? "bg-asphalt text-bone shadow-hard"
                                  : disabled
                                  ? "bg-bone text-ink/40 line-through cursor-not-allowed"
                                  : "bg-bone hover:-translate-y-0.5 hover:shadow-hard"
                              }`}
                            >
                              {v.title || "Default"}
                            </button>
                          );
                        })}
                      </div>
                      {selected && !selected.available && (
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra mt-2">
                          ✦ That one's gone. Try another.
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center border-2 border-asphalt bg-bone">
                      <button
                        onClick={() => setQty(Math.max(1, qty - 1))}
                        data-testid="qty-decrement"
                        className="p-3 hover:bg-asphalt hover:text-bone transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <div className="px-5 py-2 font-display text-2xl" data-testid="qty-value">
                        {qty}
                      </div>
                      <button
                        onClick={() => setQty(qty + 1)}
                        data-testid="qty-increment"
                        className="p-3 hover:bg-asphalt hover:text-bone transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        add(product, qty, { variantId: selected?.id });
                        toast.success(
                          `${product.name}${selected?.title && selected.title !== "Default Title" ? ` — ${selected.title}` : ""} × ${qty} added to the bag.`
                        );
                      }}
                      disabled={!inStock}
                      data-testid="product-add-to-cart"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-terra text-bone border-2 border-asphalt px-6 py-3 font-display tracking-wider uppercase text-xl shadow-hard hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_#1C1B1A] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:translate-x-0"
                    >
                      <ShoppingBag className="w-5 h-5" /> Add to Bag
                    </button>
                  </div>
                </>
              );
            })()}
            <Link
              to="/cart"
              data-testid="pdp-view-cart"
              className="inline-block border-2 border-asphalt px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone transition-colors"
            >
              View cart
            </Link>

            {/* Artist sidecard (compact, sits in buybox column on desktop) */}
            {artist && <ArtistSideCard artist={artist} role="Art / Pro" />}
          </div>
        </div>
      </section>

      {/* Story behind the graphic */}
      {product.story && (
        <section className="bg-card-light border-b-4 border-asphalt" data-testid="pdp-story">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-3">
                ✦ The Story Behind The Graphic
              </div>
              <h2 className="font-western text-4xl sm:text-5xl">How It Came To Be</h2>
            </div>
            <div className="lg:col-span-8">
              <p className="font-body text-lg leading-relaxed text-ink whitespace-pre-line">
                {product.story}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Product video */}
      {product.video_url && (
        <section className="bg-asphalt text-bone border-b-4 border-asphalt" data-testid="pdp-video">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
            <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-3">
              ✦ Moving Picture
            </div>
            <h2 className="font-western text-4xl sm:text-5xl mb-6">See It In Motion</h2>
            <div className="border-4 border-bone shadow-hard-bone bg-asphalt aspect-video overflow-hidden">
              {isUploadedVideo(product.video_url) ? (
                <video
                  src={resolveUrl(product.video_url)}
                  controls
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="pdp-video-player"
                  aria-label={`${product.name} promo video`}
                />
              ) : yt ? (
                <iframe
                  src={`https://www.youtube.com/embed/${yt}`}
                  title={`${product.name} video`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="pdp-video-iframe"
                />
              ) : (
                <a
                  href={product.video_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center w-full h-full font-mono text-xs uppercase tracking-[0.25em] underline"
                >
                  Open video →
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Related products */}
      {related.length > 0 && (
        <section className="bg-bone" data-testid="pdp-related">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-2">
                  ✦ Also In The Pile
                </div>
                <h2 className="font-western text-4xl sm:text-5xl">Related Goods</h2>
              </div>
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 border-2 border-asphalt px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone transition-colors"
              >
                Browse all
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {related.slice(0, 8).map((p, i) => (
                <Link
                  to={`/shop/${p.slug}`}
                  key={p.id}
                  data-testid={`pdp-related-${i}`}
                  className="block border-2 border-asphalt bg-card-light shadow-hard hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all"
                >
                  {p.image && (
                    <div className="aspect-square overflow-hidden border-b-2 border-asphalt bg-bone">
                      <img
                        src={resolveUrl(p.image)}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-3 text-center">
                    <div className="font-display text-base uppercase tracking-tight truncate">{p.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink mt-1">
                      ${Number(p.price).toFixed(2)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

const ArtistSideCard = ({ artist, role }) => (
  <Link
    to={`/crew/${artist.slug}`}
    data-testid="pdp-artist-card"
    className="mt-10 block border-4 border-asphalt bg-card-light shadow-hard hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all"
  >
    <div className="grid grid-cols-3">
      <div className="col-span-1 aspect-square overflow-hidden border-r-2 border-asphalt bg-asphalt">
        {artist.photo && (
          <img
            src={resolveUrl(artist.photo)}
            alt={artist.name}
            className="w-full h-full object-cover ink-stamp"
            style={{ filter: "grayscale(100%) contrast(1.2)" }}
          />
        )}
      </div>
      <div className="col-span-2 p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-terra">
          ✦ {role || "By"}
        </div>
        <div className="font-western text-xl leading-tight mt-1">&ldquo;{artist.alias}&rdquo;</div>
        <div className="font-display text-lg uppercase tracking-tight leading-tight">{artist.name}</div>
        {artist.hometown && (
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink mt-1">
            Hails from {artist.hometown}
          </div>
        )}
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-asphalt mt-3 underline">
          View dossier →
        </div>
      </div>
    </div>
  </Link>
);
